import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Globe, BarChart3, ArrowRight, Star, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SearchHero from '../components/SearchHero';
import SiteCard from '../components/SiteCard';
import CategoryBadge from '../components/CategoryBadge';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchCategories, fetchTopSites } from '../store/actions/siteActions';
import { useLocale } from '../context/LocaleContext';
import { useT } from '../hooks/useT';
import { sitePathSlug } from '../utils/sitePathSlug';
import SeoHead from '../components/SeoHead';
import AdBanner from '../components/ads/AdBanner';
import AdCarousel from '../components/ads/AdCarousel';
import AdCard from '../components/ads/AdCard';
import AdPopup from '../components/ads/AdPopup';
import { bannerAds, carouselAds, cardAds, sidebarAds } from '../data/adData';
import styled from 'styled-components';

const stats = [
  { icon: Globe, label: 'Websites Indexed', value: '2.4M+', color: 'primary' },
  { icon: TrendingUp, label: 'Similarity Queries', value: '18M+', color: 'crimson' },
  { icon: BarChart3, label: 'Categories', value: '120+', color: 'gold' },
];

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatCard = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
`;

const StatIconBox = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color, theme }) => {
    if ($color === 'primary') return 'rgba(0,48,135,0.1)';
    if ($color === 'crimson') return 'rgba(220,38,38,0.1)';
    return 'rgba(234,179,8,0.1)';
  }};
  color: ${({ $color, theme }) => {
    if ($color === 'primary') return theme.colors.primary;
    if ($color === 'crimson') return theme.colors.crimson;
    return theme.colors.gold;
  }};
`;

const StatValue = styled.p`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0;
`;

const Section = styled.section`
  padding: 2.5rem 0;
`;

const AdCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const SectionLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.crimson};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  &:hover {
    text-decoration: underline;
  }
`;

const CategoryWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const SitesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const HeaderWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $crimson, $gold, theme }) => ($crimson ? 'rgba(220,38,38,0.1)' : $gold ? 'rgba(234,179,8,0.1)' : 'transparent')};
  color: ${({ $crimson, $gold, theme }) => ($crimson ? theme.colors.crimson : $gold ? theme.colors.gold : 'inherit')};
`;

const CtaBanner = styled.div`
  background: ${({ theme }) => theme.colors.maroon};
  border-radius: ${({ theme }) => theme.radiusSm};
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    padding: 3.5rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const CtaTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 800;
  color: ${({ theme }) => theme.colors.maroonForeground};
  margin: 0;
  @media (min-width: 768px) {
    font-size: ${({ theme }) => theme.fontClamp['3xl']};
  }
`;

const CtaDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: rgba(255, 255, 255, 0.7);
  margin: 0.5rem 0 0 0;
  max-width: 32rem;
`;

const CtaBtn = styled(Link)`
  padding: 1rem 2rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.gold};
  color: ${({ theme }) => theme.colors.goldForeground};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const TopList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TopRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const RankNum = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  width: 2rem;
  text-align: right;
  color: ${({ $gold, $maroon, theme }) => ($gold ? theme.colors.gold : $maroon ? theme.colors.maroon : theme.colors.mutedForeground)};
`;

const TopInitial = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`;

const TopInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TopTitle = styled.h3`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  margin: 0;
`;

const TopDomain = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const UserScoreSpan = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
  & strong {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const TrendingPill = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  background: ${({ $rising, $top }) => ($top ? 'rgba(234,179,8,0.15)' : $rising ? 'rgba(34,197,94,0.15)' : 'rgba(0,0,0,0.06)')};
  color: ${({ $rising, $top, theme }) => ($top ? theme.colors.gold : $rising ? 'hsl(142,71%,45%)' : theme.colors.mutedForeground)};
`;

const ScorePill = styled.span`
  padding: 0.25rem 0.75rem;
  background: rgba(220, 38, 38, 0.1);
  color: ${({ theme }) => theme.colors.crimson};
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

function Index() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const { topSites, categories } = useSelector((state) => state.sites);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchTopSites({ limit: 12 }));
  }, [dispatch]);

  return (
    <PageLayout>
      <SeoHead
        title="CiteMatch | Find similar websites"
        description="Discover similar websites, alternatives, and competitors. Search by domain and explore top sites and categories."
      />
      <Navbar />
      <SearchHero />

      <Section style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
        <Container>
          <AdBanner ad={bannerAds[0]} />
        </Container>
      </Section>

      <Section>
        <Container>
          <StatGrid>
            {stats.map((stat) => (
              <StatCard
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <StatIconBox $color={stat.color}>
                  <stat.icon size={20} strokeWidth={2.5} />
                </StatIconBox>
                <div>
                  <StatValue>{stat.value}</StatValue>
                  <StatLabel>{stat.label}</StatLabel>
                </div>
              </StatCard>
            ))}
          </StatGrid>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeader>
            <SectionTitle>Browse by Category</SectionTitle>
            <SectionLink to={prependPath('/categories')}>View All <ArrowRight size={14} /></SectionLink>
          </SectionHeader>
          <CategoryWrap>
            {categories.map((cat) => (
              <CategoryBadge key={cat.slug || cat._id || cat.name} name={cat.name} slug={cat.slug} />
            ))}
          </CategoryWrap>
        </Container>
      </Section>

      <Section style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
        <Container>
          <AdCarousel ads={carouselAds} />
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeader>
            <HeaderWithIcon>
              <IconBox $crimson>
                <Flame size={20} strokeWidth={2.5} />
              </IconBox>
              <SectionTitle>Trending Sites</SectionTitle>
            </HeaderWithIcon>
            <SectionLink to={prependPath('/top-sites')}>See Rankings <ArrowRight size={14} /></SectionLink>
          </SectionHeader>
          <SitesGrid>
            {topSites.slice(0, 6).map((site) => (
              <SiteCard key={site.id} site={site} />
            ))}
          </SitesGrid>
        </Container>
      </Section>

      <Section style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
        <Container>
          <AdCardsGrid>
            {cardAds.map((ad) => (
              <AdCard key={ad.id} ad={ad} />
            ))}
          </AdCardsGrid>
        </Container>
      </Section>

      <Section>
        <Container>
          <CtaBanner>
            <div>
              <CtaTitle>{t('submitPage.ctaTitle')}</CtaTitle>
              <CtaDesc>{t('submitPage.ctaDesc')}</CtaDesc>
            </div>
            <CtaBtn to={prependPath('/submit')}>{t('submitPage.ctaButton')} <ArrowRight size={16} /></CtaBtn>
          </CtaBanner>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeader>
            <HeaderWithIcon>
              <IconBox $gold>
                <Star size={20} strokeWidth={2.5} />
              </IconBox>
              <SectionTitle>Top Ranked</SectionTitle>
            </HeaderWithIcon>
          </SectionHeader>
          <TopList>
            {topSites.slice(0, 5).map((site, i) => (
              <motion.div
                key={site.id}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <TopRow to={`/site/${sitePathSlug(site)}`}>
                  <RankNum $gold={i === 0} $maroon={i === 2}>{i + 1}</RankNum>
                  <TopInitial>{site.title.charAt(0)}</TopInitial>
                  <TopInfo>
                    <TopTitle>{site.title}</TopTitle>
                    <TopDomain>{site.domain}</TopDomain>
                  </TopInfo>
                  {site.trending && (
                    <TrendingPill $rising={site.trending === 'Rising'} $top={site.trending === 'Top choice'}>
                      {site.trending === 'Rising' && '↑ '}{site.trending}
                    </TrendingPill>
                  )}
                  <UserScoreSpan>
                    {site.userScore != null && <strong>{site.userScore}/5</strong>}
                  </UserScoreSpan>
                  <ScorePill>{site.similarityScore}%</ScorePill>
                </TopRow>
              </motion.div>
            ))}
          </TopList>
        </Container>
      </Section>

      <Section style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
        <Container>
          <AdBanner ad={bannerAds[1]} />
        </Container>
      </Section>

      <Footer />
      <AdPopup ad={sidebarAds[0]} delay={10000} />
    </PageLayout>
  );
}

export default Index;
